import React, { useState, useEffect } from 'react';
import './ImageGeneratorModal.css';

interface ImageGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (prompt: string, provider?: 'gemini' | 'grok') => Promise<void>;
  initialPrompt?: string;
  isGenerating: boolean;
  hintText?: string;
}

const ImageGeneratorModal: React.FC<ImageGeneratorModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  initialPrompt = '',
  isGenerating,
  hintText = 'Provide a detailed description of the image you want to generate.'
}) => {
  const [imagePrompt, setImagePrompt] = useState(initialPrompt);
  const [provider, setProvider] = useState<'gemini' | 'grok'>('gemini');

  // Update prompt when initialPrompt changes
  useEffect(() => {
    if (isOpen) {
      setImagePrompt(initialPrompt);
    }
  }, [initialPrompt, isOpen]);

  const handleGenerate = async () => {
    if (!imagePrompt.trim()) return;
    try {
      await onGenerate(imagePrompt.trim(), provider);
    } catch (error) {
      console.error('Error generating image:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="image-generator-modal-overlay" 
        onClick={() => !isGenerating && onClose()}
      />
      <div className="image-generator-modal">
        <div className="image-generator-modal-header">
          <h2 className="image-generator-modal-title">Generate Image</h2>
          <button 
            className="image-generator-modal-close" 
            onClick={onClose}
            disabled={isGenerating}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="image-generator-modal-content">
          <label htmlFor="image-provider" className="image-generator-modal-label">
            AI Provider
          </label>
          <div className="image-generator-modal-provider-selector">
            <button
              className={`image-generator-modal-provider-btn ${provider === 'gemini' ? 'active' : ''}`}
              onClick={() => setProvider('gemini')}
              disabled={isGenerating}
              type="button"
            >
              Gemini
            </button>
            <button
              className={`image-generator-modal-provider-btn ${provider === 'grok' ? 'active' : ''}`}
              onClick={() => setProvider('grok')}
              disabled={isGenerating}
              type="button"
            >
              Grok
            </button>
          </div>

          <label htmlFor="image-prompt" className="image-generator-modal-label">
            Image Description
          </label>
          <textarea
            id="image-prompt"
            className="image-generator-modal-textarea"
            placeholder="Describe the image you want to generate..."
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            disabled={isGenerating}
            rows={4}
          />
          <p className="image-generator-modal-hint">
            {hintText}
          </p>
        </div>

        <div className="image-generator-modal-footer">
          <button 
            className="image-generator-modal-btn image-generator-modal-btn-cancel"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button 
            className="image-generator-modal-btn image-generator-modal-btn-generate"
            onClick={handleGenerate}
            disabled={isGenerating || !imagePrompt.trim()}
          >
            {isGenerating ? 'Generating...' : 'Generate Image'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ImageGeneratorModal;

